

import React, { useState } from "react";
import {
  Box, Grid, Card, Typography, TextField, Button, IconButton, 
  InputAdornment, Checkbox, CircularProgress, FormControlLabel,
  Stepper, Step, StepLabel, MenuItem
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomDialog from "../../../CustomDialog";
import addressConfig from "../../../Configurations/addressConfig.json";
import { useNavigate } from "react-router-dom";
import ayujalogo from "../../Logos/AuthScreens/AuthLogo.png";

const COMMUNITY_OPTIONS = addressConfig.communities;
const BLOCK_OPTIONS = addressConfig.blocks;
const COMMUNITY_DETAILS = addressConfig.communityDetails;
const API_BASE = process.env.REACT_APP_API_URL;


export const fetchUserFromRedis = async (userId, token) => {
  if (!userId || !token) {
    console.log(" No userId or token provided");
    return null;
  }
  
  try {
    console.log(" Fetching user data for userId:", userId);
    
    const response = await fetch(`${API_BASE}/user/${userId}`, {
      method: 'GET',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log(" User data fetched successfully:", data.user);
    return data.user;
    
  } catch (error) {
    console.error(" Error fetching user from Redis:", error);
    return null;
  }
};

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: "resident",
    address: {
      full_name: "",
      phone: "",
      community_name: "",
      address_line1: "",
      address_line2: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      address_type: "home"
    },
    emergency_contacts: [{ name: "", phone: "" }],
    family_members: [{ name: "", relation: "", age: "", gender: "" }],
  });

  const [dialog, setDialog] = useState({ 
    open: false, 
    message: "", 
    title: "",
    type: "info" 
  });

  const steps = ['Basic Info', 'Address', 'Emergency Contacts', 'Family Members', 'Review'];

  // Handle basic input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle address field changes
  const handleAddressChange = (field, value) => {
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        [field]: value
      }
    });
  };

  // Emergency contact handlers
  const handleEmergencyChange = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index][field] = value;
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addEmergencyContact = () => {
    setFormData({
      ...formData,
      emergency_contacts: [...formData.emergency_contacts, { name: "", phone: "" }]
    });
  };

  const removeEmergencyContact = (index) => {
    const updated = formData.emergency_contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, emergency_contacts: updated });
  };

  // Family member handlers
  const handleFamilyChange = (index, field, value) => {
    const updated = [...formData.family_members];
    updated[index][field] = value;
    setFormData({ ...formData, family_members: updated });
  };

  const addFamilyMember = () => {
    setFormData({
      ...formData,
      family_members: [...formData.family_members, { name: "", relation: "", age: "", gender: "" }]
    });
  };

  const removeFamilyMember = (index) => {
    const updated = formData.family_members.filter((_, i) => i !== index);
    setFormData({ ...formData, family_members: updated });
  };

  // Validation for each step
  const validateStep = (step) => {
    switch (step) {
      case 0: // Basic Info
        if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword ) {
          setDialog({ 
            open: true, 
            type: "error",
            title: "Validation Error", 
            message: "Please fill all required fields" 
          });
          return false;
        }

      if (formData.name.trim().length < 3) {
        setDialog({
          open: true,
          type: "error",
          title: "Invalid Name",
          message: "Name must be at least 3 characters long",
        });
        return false;
      }
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(formData.password)) {
          setDialog({ 
            open: true, 
            type: "error",
            title: "Weak Password", 
            message: "Password should not start with a special character. It must contain at least one uppercase letter, one number, one special character, and be at least 8 characters long." 
          });
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setDialog({ 
            open: true, 
            type: "error",
            title: "Password Mismatch", 
            message: "Passwords do not match" 
          });
          return false;
        }
        if (!formData.termsAccepted) {
          setDialog({ 
            open: true, 
            type: "error",
            title: "Validation Error", 
            message: "Please accept the terms and conditions" 
          });
          return false;
        }
        return true;
      
      case 1: // Address 
      const requiredFields = ['community_name', 'address_line1', 'address_line2', 'city', 'state', 'pincode'];
      const missingFields = requiredFields.filter(field => !formData.address[field]);
      
      if (missingFields.length > 0) {
        setDialog({ 
          open: true, 
          type: "error",
          title: "Required Fields Missing", 
          message: "Please fill in all required address fields" 
        });
        return false;
      }
      
      // Validate pincode (6 digits)
      if (!/^\d{6}$/.test(formData.address.pincode)) {
        setDialog({ 
          open: true, 
          type: "error",
          title: "Invalid Pincode", 
          message: "Pincode must be 6 digits" 
        });
        return false;
      }
      
      // Validate phone (10 digits) - now using main phone
      if (!/^\d{10}$/.test(formData.address.phone)) {
        setDialog({ 
          open: true, 
          type: "error",
          title: "Invalid Phone", 
          message: "Phone must be 10 digits" 
        });
        return false;
      }
      
      return true;


      case 2: // Emergency Contacts (optional)
        const hasEmergencyData = formData.emergency_contacts.some(
          contact => contact.name || contact.phone
        );
        if (hasEmergencyData) {
          const incompleteContact = formData.emergency_contacts.some(
            contact => (contact.name && !contact.phone) || (!contact.name && contact.phone)
          );
          if (incompleteContact) {
            setDialog({ 
              open: true, 
              type: "warning",
              title: "Incomplete Data", 
              message: "Please complete all emergency contact fields or leave them empty to skip" 
            });
            return false;
          }
        }
        return true;

      case 3: // Family Members (optional)
        const hasFamilyData = formData.family_members.some(
          member => member.name || member.relation || member.age || member.gender
        );
        if (hasFamilyData) {
          const incompleteMember = formData.family_members.some(
            member => {
              const filled = [member.name, member.relation, member.age, member.gender].filter(Boolean).length;
              return filled > 0 && filled < 4;
            }
          );
          if (incompleteMember) {
            setDialog({ 
              open: true, 
              type: "warning",
              title: "Incomplete Data", 
              message: "Please complete all family member fields or leave them empty to skip" 
            });
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      // Prefill address name and phone when moving to address step
      if (activeStep === 0) {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            full_name: prev.name,
            phone: prev.phone
          }
        }));
      }
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSkip = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  // Handle form submit
  
  const handleSubmit = async () => {
    setLoading(true);

    try {
      const filteredEmergencyContacts = formData.emergency_contacts
        .filter(contact => contact.name && contact.phone)
        .map(contact => `${contact.name}:${contact.phone}`);

      const filteredFamilyMembers = formData.family_members
        .filter(member => member.name && member.relation && member.age && member.gender)
        .map(member => `${member.name}:${member.relation}:${member.age}:${member.gender}`);
      const addressData = {
        full_name: formData.address.full_name,
        phone: formData.address.phone,
        community_name: formData.address.community_name,
        address_line1: formData.address.address_line1,
        address_line2: formData.address.address_line2,
        landmark: formData.address.landmark || "",
        city: formData.address.city,
        state: formData.address.state,
        pincode: formData.address.pincode,
        address_type: formData.address.address_type || "home"
      };

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: formData.role,
        address: addressData,
        emergency_contacts: filteredEmergencyContacts,
        family_members: filteredFamilyMembers,
      };

      const signupAPI = process.env.REACT_APP_REGISTER_URL;
      
      const response = await fetch(signupAPI, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setRegistrationSuccess(true);
        
        // Check if user needs approval
        if (data.user.approval_status === "pending") {
          setDialog({ 
            open: false
          });
        } else {
          // Auto-approved (admin/staff)
          if (data.token) {
            sessionStorage.setItem("token", data.token);
          }
          sessionStorage.setItem("userId", data.user.userId);
          
          setTimeout(() => {
            const userRole = data.user.role?.toLowerCase().trim();
            if (userRole === "resident") {
              sessionStorage.clear();
              setDialog({ 
                open: true,
                type: "success",
                title: "Registration Completed",
                message: "Registration completed. Please wait while our team verifies your details. We will notify you once the verification is done.",
                onPrimaryAction: () => {
                  navigate("/", { replace: true });

                }
              });
             
            } else {
              window.location.href = "/admin-dashboard";
            }
          }, 2000);
        }
      } else {
        setDialog({ 
          open: true, 
          type: "error",
          title: "Registration Failed", 
          message: data.error || "Something went wrong" 
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setDialog({ 
        open: true, 
        type: "error",
        title: "Registration Failed", 
        message: error.message || "Something went wrong. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };
const applyCommunityDetails = (community) => {
  if (COMMUNITY_DETAILS?.[community]) {
    const details = COMMUNITY_DETAILS[community];
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        address_line1: "",
        address_line2: "",
        landmark: "",
        community_name: community,
        city: details.city || "",
        state: details.state || "",
        pincode: details.pincode || ""
      }
    }));
  }
};

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Basic Info
        return (
          <>
              <TextField
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only alphabets and spaces
                  if (/^[A-Za-z ]*$/.test(value)) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter your full name"
                fullWidth
                margin="normal"
                required
              />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  email: e.target.value.toLowerCase(),
                })
              }
              placeholder="Enter your email address"
              fullWidth
              margin="normal"
              required
            />

            <TextField
              label="Mobile Number"
              name="phone"
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setFormData({ ...formData, phone: value });
              }}
              placeholder="Enter 10-digit mobile number"
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">+91</InputAdornment>,
              }}
            />

            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              fullWidth
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              fullWidth
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.termsAccepted || false}
                    onChange={(e) =>
                      setFormData({ ...formData, termsAccepted: e.target.checked })
                    }
                  />
                }
                label={
                  <span>
                    Accept Ayuja's{" "}
                    <a
                      href="/terms&conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#007a85",
                        fontWeight: "bold",
                        cursor: "pointer",
                        textDecoration: "none"
                      }}
                    >
                      Terms and Conditions
                    </a>
                  </span>
                }
              />
          </>
        );

        case 1: // Address
          return (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: "#007a85" }}>
              Address (Required) 
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                Add your address to deliver medicine. You can skip this step and add it later.
              </Typography>

              <Grid container spacing={2} direction="column">
                <Grid item xs={12} sx={{ display: "none" }}>
                  <TextField
                    label="Full Name"
                    value={formData.address.full_name}
                    onChange={(e) => handleAddressChange("full_name", e.target.value)}
                    placeholder="Enter recipient's full name"
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sx={{ display: "none" }}>
                  <TextField
                    label="Phone Number"
                    value={formData.address.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                      handleAddressChange("phone", value);
                    }}
                    placeholder="Enter 10-digit phone number"
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    required
                    label="Community/Society Name"
                    value={formData.address.community_name}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Select Community</MenuItem>

                    {COMMUNITY_OPTIONS.map((community) => (
                      <MenuItem
                        key={community}
                        value={community}
                        onClick={() => applyCommunityDetails(community)}
                      >
                        {community}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* BLOCK DROPDOWN - NO AUTO-FILL LOGIC HERE */}
                <Grid item xs={12}>
                  <TextField
                    select
                    required
                    label="Block"
                    value={formData.address.address_line1}
                    onChange={(e) => handleAddressChange("address_line1", e.target.value)}
                    placeholder="Select Block"
                    fullWidth
                    size="small"
                    error={!!errors?.block}
                    helperText={errors?.block}
                  >
                    <MenuItem value="">Select Block</MenuItem>
                    {BLOCK_OPTIONS.map((block) => (
                      <MenuItem key={block} value={block}>
                        {block}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Flat Number"
                    required
                    value={formData.address.address_line2}
                    onChange={(e) => handleAddressChange("address_line2", e.target.value)}
                    placeholder="Enter Flat Number"
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Landmark (Optional)"
                    value={formData.address.landmark}
                    onChange={(e) => handleAddressChange("landmark", e.target.value)}
                    placeholder="Nearby landmark"
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* CITY - AUTO-FILLED AND DISABLED */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    required
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange("city", e.target.value)}
                    placeholder="Enter city"
                    fullWidth
                    size="small"
                    // disabled={!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]}
                    InputProps={{
                      sx: {
                        backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) 
                          ? '#f5f5f5' 
                          : 'white'
                      }
                    }}
                  />
                </Grid>

                {/* STATE - AUTO-FILLED AND DISABLED */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="State"
                    required
                    value={formData.address.state}
                    onChange={(e) => handleAddressChange("state", e.target.value)}
                    placeholder="Enter state"
                    fullWidth
                    size="small"
                    // disabled={!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]}
                    InputProps={{
                      sx: {
                        backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) 
                          ? '#f5f5f5' 
                          : 'white'
                      }
                    }}
                  />
                </Grid>

                {/* PINCODE - AUTO-FILLED AND DISABLED */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pincode"
                    required
                    value={formData.address.pincode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      handleAddressChange("pincode", value);
                    }}
                    placeholder="6-digit pincode"
                    fullWidth
                    size="small"
                    // disabled={!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]}
                    InputProps={{
                      sx: {
                        backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) 
                          ? '#f5f5f5' 
                          : 'white'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} sx={{ display: "none" }}>
                  <TextField
                    select
                    label="Address Type"
                    value={formData.address.address_type || "home"}
                    onChange={(e) => handleAddressChange("address_type", e.target.value)}
                    fullWidth
                    size="small"
                  >
                  </TextField>
                </Grid>
              </Grid>
            </>
          );

      case 2: // Emergency Contacts
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: "#007a85" }}>
              Emergency Contacts (Optional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Add emergency contacts who we can reach in case of urgency. You can skip this step.
            </Typography>

            {formData.emergency_contacts.map((contact, idx) => (
              <Card key={idx} sx={{ mb: 2, p: 2, backgroundColor: "#f9f9f9" }}>
             <Grid container spacing={2} direction="column">
                  <Grid item xs={12} sm={5}>
            
                    <TextField
                        label="Contact Name"
                        required
                        value={contact.name}
                        onChange={(e) => handleEmergencyChange(idx, "name", e.target.value)}
                        fullWidth
                        size="small"
                        error={contact.name.length > 0 && contact.name.length < 3}
                        helperText={
                          contact.name.length > 0 && contact.name.length < 3
                            ? "Name must be at least 3 characters"
                            : ""
                        }
                      />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Phone Number"
                      required
                      value={contact.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        handleEmergencyChange(idx, "phone", value);
                      }}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    {formData.emergency_contacts.length > 1 && (
                      <IconButton color="error" onClick={() => removeEmergencyContact(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button
              variant="outlined"
              onClick={addEmergencyContact}
              sx={{
                color: "#007a85",
                borderColor: "#007a85",
                textTransform: "none",
                "&:hover": { borderColor: "#005f64" }
              }}
            >
              + Add Another Contact
            </Button>
          </>
        );

      case 3: // Family Members
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: "#007a85" }}>
              Family Members (Optional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Add family member details for better personalized care. You can skip this step.
            </Typography>

            {formData.family_members.map((member, idx) => (
              <Card key={idx} sx={{ mb: 2, p: 2, backgroundColor: "#f9f9f9" }}>
                 <Grid container spacing={2} direction="column">
                  <Grid item xs={12} sm={6}>
                 
                    <TextField
                      label="Name"
                      required
                      value={member.name}
                      onChange={(e) => handleFamilyChange(idx, "name", e.target.value)}
                      fullWidth
                      size="small"
                      error={member.name.length > 0 && member.name.length < 3}
                      helperText={
                        member.name.length > 0 && member.name.length < 3
                          ? "Name must be at least 3 characters"
                          : ""
                      }
                    />

                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Relation"
                      required
                      value={member.relation}
                      onChange={(e) => handleFamilyChange(idx, "relation", e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="e.g., Mother, Father"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Age"
                      type="number"
                      required
                      value={member.age}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || (Number(value) >= 1 && Number(value) <= 120)) {
                          handleFamilyChange(idx, "age", value);
                        }
                      }}
                      inputProps={{ min: 1, max: 120 }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Gender"
                      required
                      value={member.gender}
                      onChange={(e) => handleFamilyChange(idx, "gender", e.target.value)}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Prefer not to say</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {formData.family_members.length > 1 && (
                      <IconButton color="error" onClick={() => removeFamilyMember(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button
              variant="outlined"
              onClick={addFamilyMember}
              sx={{
                color: "#007a85",
                borderColor: "#007a85",
                textTransform: "none",
                "&:hover": { borderColor: "#005f64" }
              }}
            >
              + Add Family Member
            </Button>
          </>
        );

      case 4: // Review
        return (
          <>
            <Typography variant="h6" sx={{ mb: 3, color: "#007a85" }}>
              Review Your Information
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Personal Information
              </Typography>
              <Typography variant="body2"><strong>Name:</strong> {formData.name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> +91{formData.phone}</Typography>
            </Box>


            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Delivery Address
              </Typography>
              <Typography variant="body2"><strong>Name:</strong> {formData.address.full_name}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> +91{formData.address.phone}</Typography>
              <Typography variant="body2"><strong>Community:</strong> {formData.address.community_name}</Typography>
              <Typography variant="body2"><strong>Block:</strong> {formData.address.address_line1}</Typography>
              <Typography variant="body2"><strong>Flat:</strong> {formData.address.address_line2}</Typography>
              <Typography variant="body2">
                {formData.address.city}, {formData.address.state} - {formData.address.pincode}
              </Typography>
              {formData.address.landmark && (
                <Typography variant="body2"><strong>Landmark:</strong> {formData.address.landmark}</Typography>
              )}
            </Box>



            {formData.emergency_contacts.some(c => c.name && c.phone) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Emergency Contacts
                </Typography>
                {formData.emergency_contacts.filter(c => c.name && c.phone).map((contact, idx) => (
                  <Typography key={idx} variant="body2">
                    {contact.name} - +91{contact.phone}
                  </Typography>
                ))}
              </Box>
            )}

            {formData.family_members.some(m => m.name && m.relation) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Family Members
                </Typography>
                {formData.family_members.filter(m => m.name && m.relation).map((member, idx) => (
                  <Typography key={idx} variant="body2">
                    {member.name} - {member.relation}, {member.age} years, {member.gender}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 5,
          }}
        >
        <Card sx={{ borderRadius: "16px",width: "60vw",height:"80vh" }}>
          <Grid container>
          {/* Left Section - Branding */}
         <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "linear-gradient(135deg, #004f54, #007a85, #007a85)",
              color: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: 4,
              width: "50%",
            }}
          >
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                <img src={ayujalogo} alt="Ayuja Logo" style={{ height: "80px", width: "auto" }} />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                Join <br /> Ayuja Today
              </Typography>

              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Transform lives through personalized in-home care. Become part of a community that
                values compassion, dignity, and exceptional service.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mb: 4 }}>
                <Typography variant="body2">✅ 24/7 Support</Typography>
                <Typography variant="body2">✅ Certified Caregivers</Typography>
              </Box>

              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  mx: "auto",
                  maxWidth: "80%"
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Emergency Support Available
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Need immediate assistance? Our emergency support team is available 24/7.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/contactus")}
                  sx={{
                    backgroundColor: "#80ED99",
                    "&:hover": { backgroundColor: "#80ED99" },
                    color: "#22577A",
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  Contact Emergency
                </Button>
              </Box>
            </Grid>

          {/* Right Section - Form */}
        <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "white",
              p: 4,
              display: "flex",
              flexDirection: "column",
              width: "50%",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              Create Account
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Get started with your personalized care dashboard
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { fontSize: '0.7rem' },
                      '& .MuiStepIcon-root': { color: activeStep >= steps.indexOf(label) ? '#007a85' : 'rgba(0, 0, 0, 0.38)' },
                      '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: '#007a85' },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <Box sx={{ mb: 3, minHeight: "400px", maxHeight: "450px", overflowY: "auto" }}>
              {renderStepContent(activeStep)}
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{ flex: 1, borderColor: "#007a85", color: "#007a85", textTransform: "none" }}
                >
                  Back
                </Button>
              )}

              {/* {activeStep > 0 && activeStep < 4 && ( */}
              {activeStep > 1 && activeStep < 4 && (
                <Button variant="text" onClick={handleSkip} sx={{ color: "#666" }}>
                  Skip
                </Button>
              )}

              {activeStep < 4 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ flex: 1, backgroundColor: "#007a85", textTransform: "none", "&:hover": { backgroundColor: "#005f64" } }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ flex: 1, backgroundColor: "#007a85", textTransform: "none", "&:hover": { backgroundColor: "#005f64" } }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Register"}
                </Button>
              )}
            </Box>

            <Typography sx={{ textAlign: "center", mt: 3 }}>
              Already have an account?{" "}
              <a href="/Login" style={{ color: "#007a85", fontWeight: "bold", textDecoration: "none" }}>
                Sign In
              </a>
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Custom Dialog */}

        <CustomDialog
          open={dialog.open}
          title={dialog.title}
          message={dialog.message}
          type={dialog.type}
          onClose={() => setDialog({ ...dialog, open: false })}
          onPrimaryAction={dialog.onPrimaryAction}
        />
    </Box>
  );
};

export default Register;